import {
  ChoosePortalContent,
  OutgoingOnlyPortalContent,
  PaymentOverdueContent,
  PortalMismatchContent,
} from "@/helpers/orgSession"
import { ModalDarkContentType } from "@/types"
import { Dispatch, SetStateAction, createContext, useContext } from "react"
import { EasetechSpinnerProps } from "../easetech-spinner/EasetechSpinner"
import { MapWebglNoticeContent } from "../google-map/helpers/mapWebglDiagnostics"
import type { DeleteConfirmationContent } from "../modal-dark/modal-dark-platform-content/delete-confirmation/DeleteConfirmation"
import type { ProjectSizeNoticeContent } from "../modal-dark/modal-dark-platform-content/project-size-notice/ProjectSizeNotice"

/** Everything `openDarkModal({ content })` can carry; each modal type reads its own shape. */
export type ModalDarkContentValue =
  | ModalDarkContentType
  | EasetechSpinnerProps
  | MapWebglNoticeContent
  | PortalMismatchContent
  | ChoosePortalContent
  | OutgoingOnlyPortalContent
  | PaymentOverdueContent
  | DeleteConfirmationContent
  | ProjectSizeNoticeContent

export type ModalDarkMode = "default" | "neutral" | "soft" | "white"

export type TopOffsetPreset = "small" | "middle" | "large" | "centered"

export const TopOffsetValues: Record<TopOffsetPreset, number | undefined> = {
  small: 30,
  middle: 60,
  large: 90,
  centered: undefined,
}

export type ModalDarkTypes =
  // 🏡 Website landing modals
  | "pricing-request"
  | "success"
  | "auto-approval-success"
  | "auto-approval-triggered"
  | "quick-contact"
  | "get-quote"
  | "more-info"
  | "full-review"
  | "loading-notice"
  | "delivery-confirmation"
  | "delivery-sent-notice"

  // 🌐 Platform modals
  | "email-sending-confirmation"
  | "success-aoi-approval"
  | "email-not-confirmed"
  | "expired-confirmation-link"
  | "email-taken"
  | "choose-portal"
  | "portal-mismatch"
  | "outgoing-only-portal"
  | "payment-overdue"
  | "single-submission"
  | "level-of-service"
  | "kmz-conversion-notice"
  | "license-upgrade-request"
  | "start-notice"
  | "zoom-notice"
  | "map-webgl-notice"
  | "license-required"
  | "auto-approval-info"
  | "submitting-confirmation"
  | "aoi-submitting-confirmation"
  | "aoi-approval-confirmation"
  | "link-successful-notice"
  | "delivery-confirmed"
  | "delivery-details"
  | "assistance-info"
  | "assistance-sent-notice"
  | "delivery-report-preview"
  | "confirm-emergency"
  | "upload-geometry-notice"
  | "project-size-notice"
  | "archive-notice"
  | "delete-confirmation"
  | "payment-success"
  | ""

export type WidthKey = "width1200" | "width900" | "width600" | "width400" | "widthUndefined"

export const WidthValues: Record<WidthKey, string | undefined> = {
  width1200: "1200px",
  width900: "900px",
  width600: "600px",
  width400: "400px",
  widthUndefined: undefined,
}

export type OpenDarkModalParams<T = ModalDarkContentType> = {
  type: ModalDarkTypes
  content?: T
  contentReviewCard?: ComponentUiReview
  isLogo?: boolean
  mode?: ModalDarkMode
  width?: WidthKey
  centered?: boolean
  topOffset?: TopOffsetPreset
}

type ContextValues = {
  isModalDarkOpen: boolean
  setIsModalDarkOpen: Dispatch<SetStateAction<boolean>>
  modalDarkTypes: ModalDarkTypes
  setModalDarkTypes: Dispatch<SetStateAction<ModalDarkTypes>>
  widthDarkModal: WidthKey
  setWidthDarkModal: Dispatch<SetStateAction<WidthKey>>
  openDarkModal: <T extends ModalDarkContentValue>(params: OpenDarkModalParams<T>) => void
  modalDarkMode: ModalDarkMode
  setModalDarkMode: Dispatch<SetStateAction<ModalDarkMode>>
  isLogo: boolean
  setIsLogo: Dispatch<SetStateAction<boolean>>
  modalDarkContent: ModalDarkContentValue | null | undefined
  setModalDarkContent: Dispatch<SetStateAction<ModalDarkContentValue | null | undefined>>
  contentReviewCard: ComponentUiReview | undefined
  onSubmit?: () => void
  setOnSubmit: (fn?: () => void) => void
  centered: boolean
  topOffset?: number
}

export const defaultModalDarkValues: ContextValues = {
  isModalDarkOpen: false,
  setIsModalDarkOpen: () => null,
  modalDarkTypes: "",
  setModalDarkTypes: () => null,
  widthDarkModal: "widthUndefined",
  setWidthDarkModal: () => null,
  openDarkModal: () => null,
  modalDarkMode: "default",
  setModalDarkMode: () => null,
  isLogo: true,
  setIsLogo: () => null,
  modalDarkContent: undefined,
  setModalDarkContent: () => null,
  contentReviewCard: undefined,
  onSubmit: undefined,
  setOnSubmit: () => null,
  centered: false,
  topOffset: 0,
}

const ModalDarkContext = createContext(defaultModalDarkValues)
const useModalDarkContext = () => useContext(ModalDarkContext)

export { ModalDarkContext, useModalDarkContext }
